import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { IconButton } from '@/components/primitives/icon-button'
import { cn } from '@/lib/cn'

type DrawerSize = 'compact' | 'sm' | 'md' | 'lg' | 'xl'

const sizeClassName: Record<DrawerSize, string> = {
  compact: 'sm:max-w-[400px]',
  sm: 'sm:max-w-[480px]',
  md: 'sm:max-w-[560px]',
  lg: 'sm:max-w-[640px]',
  xl: 'sm:max-w-[960px]',
}

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'summary',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function focusableElements(dialog: HTMLElement) {
  return Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector)).filter((element) => {
    const closedDetails = element.closest('details:not([open])')
    return (
      closedDetails === null ||
      (element.tagName === 'SUMMARY' &&
        element.parentElement === closedDetails &&
        closedDetails.parentElement?.closest('details:not([open])') === null)
    )
  })
}

function activeElementOutsideDialog() {
  if (typeof document === 'undefined' || !(document.activeElement instanceof HTMLElement)) {
    return null
  }
  return document.activeElement.closest('[role="dialog"]') === null ? document.activeElement : null
}

export interface DrawerProps {
  blockOutsideInteractions?: boolean
  bodyClassName?: string
  children: ReactNode
  closeIcon?: ReactNode
  closeLabel?: string
  closeOnEscape?: boolean
  closeOnOutsideClick?: boolean
  confirmBeforeClose?: () => boolean
  description?: string
  footer?: ReactNode
  footerClassName?: string
  headerActions?: ReactNode
  headerAddon?: ReactNode
  modal?: boolean
  open: boolean
  onCloseStart?: () => void
  onOpenChange: (open: boolean) => void
  panelClassName?: string
  size?: DrawerSize
  title: string
  titleMeta?: ReactNode
  trapFocus?: boolean
}

function CloseIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="m4.25 4.25 7.5 7.5m0-7.5-7.5 7.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.6"
      />
    </svg>
  )
}

export function Drawer({
  blockOutsideInteractions = false,
  bodyClassName,
  children,
  closeIcon,
  closeLabel = '关闭',
  closeOnEscape = true,
  closeOnOutsideClick = true,
  confirmBeforeClose,
  description,
  footer,
  footerClassName,
  headerActions,
  headerAddon,
  modal = true,
  onCloseStart,
  onOpenChange,
  open,
  panelClassName,
  size = 'md',
  title,
  titleMeta,
  trapFocus = modal,
}: DrawerProps) {
  const titleId = useId()
  const descriptionId = useId()
  const closeTimeoutRef = useRef<number | null>(null)
  const dialogRef = useRef<HTMLElement | null>(null)
  const previousOpenRef = useRef(open)
  const [initialRestoreFocusTo] = useState(activeElementOutsideDialog)
  const restoreFocusRef = useRef<HTMLElement | null>(initialRestoreFocusTo)
  const requestCloseRef = useRef(requestClose)
  const [isClosing, setIsClosing] = useState(false)

  useLayoutEffect(() => {
    requestCloseRef.current = requestClose
  })

  useLayoutEffect(() => {
    const wasOpen = previousOpenRef.current
    previousOpenRef.current = open
    if (!open || wasOpen) {
      return
    }

    const activeElement = document.activeElement
    if (activeElement instanceof HTMLElement && !dialogRef.current?.contains(activeElement)) {
      restoreFocusRef.current = activeElement
    }
  }, [open])

  useEffect(() => {
    if (open) {
      return undefined
    }

    const rememberFocus = (event: FocusEvent) => {
      if (event.target instanceof HTMLElement && event.target.closest('[role="dialog"]') === null) {
        restoreFocusRef.current = event.target
      }
    }
    const rememberPointerTarget = (event: PointerEvent) => {
      if (event.target instanceof HTMLElement) {
        restoreFocusRef.current = event.target
      }
    }
    if (restoreFocusRef.current === null && document.activeElement instanceof HTMLElement) {
      restoreFocusRef.current = document.activeElement
    }
    document.addEventListener('focusin', rememberFocus)
    document.addEventListener('pointerdown', rememberPointerTarget, true)

    return () => {
      document.removeEventListener('focusin', rememberFocus)
      document.removeEventListener('pointerdown', rememberPointerTarget, true)
    }
  }, [open])

  useEffect(() => {
    if (!open || !modal) {
      return undefined
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [modal, open])

  useEffect(
    () => () => {
      if (closeTimeoutRef.current !== null) {
        window.clearTimeout(closeTimeoutRef.current)
      }
    },
    [],
  )

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const dialog = dialogRef.current
      const openOverlays = document.querySelectorAll<HTMLElement>('[data-overlay-panel]')
      if (!dialog || openOverlays.item(openOverlays.length - 1) !== dialog) {
        return
      }

      if (event.key === 'Escape') {
        const activeElement = document.activeElement
        if (
          activeElement instanceof HTMLElement &&
          activeElement.getAttribute('role') === 'combobox' &&
          activeElement.getAttribute('aria-expanded') === 'true'
        ) {
          return
        }
        if (closeOnEscape) {
          event.stopImmediatePropagation()
          requestCloseRef.current()
        }
        return
      }

      if (event.key !== 'Tab') {
        return
      }

      if (!trapFocus) {
        return
      }

      const elements = focusableElements(dialog)
      if (elements.length === 0) {
        event.preventDefault()
        return
      }
      const first = elements[0]
      const last = elements[elements.length - 1]
      const activeElement = document.activeElement
      if (event.shiftKey && (activeElement === first || !dialog.contains(activeElement))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (activeElement === last || !dialog.contains(activeElement))) {
        event.preventDefault()
        first.focus()
      }
    }

    const dialog = dialogRef.current
    const activeElement = document.activeElement
    if (
      restoreFocusRef.current === null &&
      activeElement instanceof HTMLElement &&
      !dialog?.contains(activeElement)
    ) {
      restoreFocusRef.current = activeElement
    }
    if (dialog && !dialog.contains(document.activeElement)) {
      focusableElements(dialog)[0]?.focus()
    }
    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      const restoreFocusTo = restoreFocusRef.current
      if (restoreFocusTo?.isConnected) {
        restoreFocusTo.focus()
      }
    }
  }, [closeOnEscape, open, trapFocus])

  function requestClose() {
    if (isClosing) {
      return
    }

    if (confirmBeforeClose && !confirmBeforeClose()) {
      return
    }

    onCloseStart?.()
    setIsClosing(true)
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null
      setIsClosing(false)
      onOpenChange(false)
    }, 180)
  }

  if (!open) {
    return null
  }

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[90]',
        !modal && !blockOutsideInteractions && 'pointer-events-none',
      )}
      role="presentation"
    >
      {modal || blockOutsideInteractions ? (
        <button
          aria-label={title}
          className={cn(
            'drawer-overlay absolute inset-0 cursor-default',
            modal && 'bg-[rgba(26,28,25,0.3)]',
          )}
          data-state={isClosing ? 'closed' : 'open'}
          onClick={() => {
            if (closeOnOutsideClick) {
              requestClose()
            }
          }}
          type="button"
        />
      ) : null}
      <section
        aria-describedby={description ? descriptionId : undefined}
        aria-labelledby={titleId}
        aria-modal={modal ? 'true' : undefined}
        className={cn(
          'drawer-panel',
          'absolute top-0 right-0 flex h-dvh w-full flex-col overflow-hidden bg-white shadow-[0_0_20px_rgba(26,28,25,0.15)]',
          sizeClassName[size],
          !modal && !blockOutsideInteractions && 'pointer-events-auto',
          isClosing && 'pointer-events-none',
          panelClassName,
        )}
        data-drawer-panel
        data-overlay-panel
        data-state={isClosing ? 'closed' : 'open'}
        inert={isClosing}
        ref={dialogRef}
        role="dialog"
      >
        <header className="shrink-0 border-b border-[var(--color-divider)] px-6">
          <div
            className={cn(
              'flex items-center justify-between gap-4',
              description ? 'min-h-[72px] py-3' : headerAddon ? 'min-h-[72px] py-4' : 'h-[55px]',
            )}
          >
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2.5">
                <h2
                  className="min-w-0 truncate text-base leading-5 font-bold text-[var(--color-text)]"
                  id={titleId}
                  title={title}
                >
                  {title}
                </h2>
                {titleMeta ? (
                  <span className="shrink-0 rounded-full bg-[var(--color-hover-muted)] px-2 py-0.5 text-xs leading-5 font-semibold text-[var(--color-text-muted)]">
                    {titleMeta}
                  </span>
                ) : null}
              </div>
              {description ? (
                <p
                  className="mt-2 truncate text-sm text-[var(--color-text-muted)]"
                  id={descriptionId}
                >
                  {description}
                </p>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {headerActions}
              <IconButton
                aria-label={closeLabel}
                className="shrink-0 text-[var(--color-text-muted)]"
                onClick={requestClose}
                size="sm"
              >
                {closeIcon ?? <CloseIcon />}
              </IconButton>
            </div>
          </div>
          {headerAddon ? <div className="pb-5">{headerAddon}</div> : null}
        </header>
        <div className={cn('min-h-0 flex-1', bodyClassName ?? 'overflow-y-auto px-6 py-6')}>
          {children}
        </div>
        {footer ? (
          <footer
            className={cn(
              'flex min-h-[86px] shrink-0 items-center justify-end gap-3 border-t border-[var(--color-divider)] bg-white px-6 py-5',
              footerClassName,
            )}
          >
            {footer}
          </footer>
        ) : null}
      </section>
    </div>,
    document.body,
  )
}
